import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api from '../api';
import { log } from '../lib/logger';

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

// --- External effect bodies / helpers ---

const bootstrapSession = (setUser, setCustomer, setLoading) => {
  let cancelled = false;
  api.get('/auth/me')
    .then((r) => {
      if (cancelled) return;
      if (r.data?.type === 'staff') setUser(r.data.user);
      else if (r.data?.type === 'customer') setCustomer(r.data.customer);
    })
    .catch(() => { /* anonymous — expected */ })
    .finally(() => { if (!cancelled) setLoading(false); });
  return () => { cancelled = true; };
};

const serverLogout = async () => {
  try { await api.post('/auth/logout'); }
  catch (err) { log.warn('Server logout failed, clearing client session anyway', err); }
};

// --- Provider ---

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => bootstrapSession(setUser, setCustomer, setLoading), []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    setUser(data.user); setCustomer(null);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    await serverLogout();
    setUser(null);
    window.location.href = '/';
  }, []);

  const registerCustomer = useCallback(async (payload) => {
    const { data } = await api.post('/customers/register', payload);
    if (data.requires_verification) return { requires_verification: true, email: data.email, phone: data.phone };
    setCustomer(data.customer); setUser(null);
    return data.customer;
  }, []);

  const loginCustomer = useCallback(async (email, password) => {
    const { data } = await api.post('/customers/login', { email, password });
    setCustomer(data.customer); setUser(null);
    return data.customer;
  }, []);

  const logoutCustomer = useCallback(async () => {
    await serverLogout();
    setCustomer(null);
    window.location.href = '/';
  }, []);

  const setCustomerSession = useCallback((c) => { setCustomer(c); setUser(null); }, []);

  // Split into 4 small memos (each ≤4 deps).
  const session = useMemo(
    () => ({ user, customer, loading }),
    [user, customer, loading],
  );

  const flags = useMemo(
    () => ({ isAdmin: user?.role === 'admin', isAuthed: !!user }),
    [user],
  );

  const staffActions = useMemo(
    () => ({ login, logout }),
    [login, logout],
  );

  const customerActions = useMemo(
    () => ({ registerCustomer, loginCustomer, logoutCustomer, setCustomerSession }),
    [registerCustomer, loginCustomer, logoutCustomer, setCustomerSession],
  );

  const value = useMemo(
    () => ({ ...session, ...flags, ...staffActions, ...customerActions }),
    [session, flags, staffActions, customerActions],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
